import authService from './authService';
import { buildHeaders } from "src/utils/apiUtils";


let BASE_URL = process.env.REACT_APP_API_URL

export async function loadConfig() {
  // .env is used directly; no encrypted config to load.
}

loadConfig().then(() => {
});


export async function getFunctions(): Promise<any> {
    try {
        const headers = await buildHeaders(BASE_URL);
        const response = await fetch(`${BASE_URL}/functions/getAll`, {
            method: "GET",
            headers,
            credentials: "include",
        });




        const data = await response.json();
        if (!response.ok || !data.success) {
            if (response.status === 401 || response.status === 403 || data.message === "Forbidden access") {
                throw new Error("Forbidden: You do not have permission to access this resource.");
            }
            //console.log("Failed to fetch");
        }
        return data;
    } catch (error: any) {
        //console.log(error);
    }
}

export async function getUserInThisFunction(function_id:any): Promise<any> {
    try {
        const headers = await buildHeaders(BASE_URL);
        const response = await fetch(`${BASE_URL}/user-functions/getUserInThisFunction/${function_id}`,{
            method: "GET",
            headers,
            credentials: "include",
        });
        const data = await response.json();
        if (!response.ok ) {
            if (response.status === 401 || response.status === 403 || data.message === "Forbidden access") {
                throw new Error("Forbidden: You do not have permission to access this resource.");
            }
            throw new Error("Failed to fetch");
        }
        return data;
    } catch (error: any) {
        throw new Error(error);
    }
}




export async function getFunctionsOrganization(
  query: string = '',
  perPage: number = 500,
  pageNo: number = 0
): Promise<any> {
  try {
    const response = await authService.apiCall(
      `${BASE_URL}/functions/getAll?query=${query}&perPage=${perPage}&pageNo=${pageNo}`,
      { method: 'GET' }
    );
    const data = await response.json();
    if (!response.ok || !data.success) {
      if (response.status === 401 || response.status === 403 || data.message === "Forbidden access") {
        throw new Error("Forbidden: You do not have permission to access this resource.");
      }
      throw new Error("Failed to fetch");
    }
    return data;
  } catch (error: any) {
    throw new Error(error?.message || "Failed to fetch");
  }
}

/** Functions with their assigned users in one call (single backend query) — avoids one request per function. */
export async function getFunctionsWithUsers(): Promise<any> {
  try {
    const response = await authService.apiCall(
      `${BASE_URL}/functions/getAllWithUsers`,
      { method: 'GET' }
    );
    const data = await response.json();
    if (!response.ok || !data.success) {
      if (response.status === 401 || response.status === 403 || data.message === "Forbidden access") {
        throw new Error("Forbidden: You do not have permission to access this resource.");
      }
      throw new Error("Failed to fetch");
    }
    return data;
  } catch (error: any) {
    throw new Error(error?.message || "Failed to fetch");
  }
}

export async function updateUserFunctions(userId: string, functionIds: string[]): Promise<any> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/user-functions/${userId}/functions`, {
      method: 'POST',
      body: JSON.stringify({ functionIds }),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      if (
        response.status === 401 ||
        response.status === 403 ||
        data.message === "Forbidden access"
      ) {
        throw new Error("Forbidden: You do not have permission to access this resource.");
      }
      throw new Error(data.message || "Failed to update functions.");
    }
    return data;
  } catch (error: any) {
    throw new Error(error?.message || "Unexpected error occurred while updating functions.");
  }
}

/** Every user as { id, name } for a picker (GET /users/lookup, show-all). */
export async function getUsersForSelect(): Promise<Array<{ id: string; name: string }>> {
  const response = await authService.apiCall(`${BASE_URL}/users/lookup`, { method: 'GET' });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success) throw new Error(data.message || 'Failed to fetch users');
  return Array.isArray(data.data) ? data.data : [];
}

export async function getUsersWithFunctions(): Promise<Array<{ id: string; name: string }>> {
  try {
    const response = await authService.apiCall(
      `${BASE_URL}/user-functions/users-with-functions`,
      { method: 'GET' }
    );
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.message || "Failed to fetch");
    }
    const list = Array.isArray(data.data) ? data.data : [];
    return list.map((u: { id: string; name?: string }) => ({ id: u.id, name: u.name || u.id }));
  } catch (error: any) {
    throw new Error(error?.message || "Failed to fetch function users");
  }
}

export async function getFunctionsForSelect(): Promise<Array<{ id: string; name: string }>> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/functions/forSelect`, { method: 'GET' });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to fetch functions');
    }
    return Array.isArray(data.data) ? data.data : [];
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to fetch functions for select');
  }
}

export async function getFunctionsForUser(userId: string): Promise<string[]> {
  try {
    const response = await authService.apiCall(
      `${BASE_URL}/user-functions/${userId}`,
      { method: 'GET' }
    );
    const data = await response.json();
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new Error('Forbidden');
      }
      return [];
    }
    const user = data.data || data;
    if (!user) return [];
    const raw = user.functions || user.Functions || [];
    const list = Array.isArray(raw) ? raw : [];
    return list
      .map((f: any) => f.functionId || f.Function?.id || f.id)
      .filter(Boolean);
  } catch (error: any) {
    console.warn('getFunctionsForUser error:', error?.message);
    return [];
  }
}

/**
 * The logged-in user's own function id(s) — GET /user-functions/my-functions.
 * Self-scoped (resolves the caller from the auth token), unlike
 * getFunctionsForUser(userId) which requires an admin permission the caller
 * may not have when looking up their own membership.
 */
export async function getMyFunctionIds(): Promise<string[]> {
  try {
    const response = await authService.apiCall(
      `${BASE_URL}/user-functions/my-functions`,
      { method: 'GET' }
    );
    const data = await response.json();
    if (!response.ok || !data.success) return [];
    const rows = Array.isArray(data.data) ? data.data : [];
    return rows
      .map((row: any) => row.functionId || row.Function?.id)
      .filter(Boolean);
  } catch (error: any) {
    console.warn('getMyFunctionIds error:', error?.message);
    return [];
  }
}
